import { loadDatevOverview } from '../../../datev/datevOverview';
import { METAL_QUOTE_SOURCE_LABELS, type MetalQuoteSource } from '../../../../utils/metalQuotes';
import {
  SETTINGS_PATH_DATEV,
  SETTINGS_PATH_INTEGRATION_DKV,
  SETTINGS_PATH_INTEGRATION_IDS,
  SETTINGS_PATH_INTEGRATION_MEINSHK,
  SETTINGS_PATH_INTEGRATION_METAL,
  SETTINGS_PATH_INTEGRATION_OMD,
  SETTINGS_PATH_USERS_API,
} from '../../../../utils/settingsRoutes';
import type { IntegrationState } from './integrationUi';

export type Category = 'Buchhaltung' | 'Großhandel & Artikel' | 'Fuhrpark' | 'Schnittstellen';
export const CATEGORY_ORDER: Category[] = ['Buchhaltung', 'Großhandel & Artikel', 'Fuhrpark', 'Schnittstellen'];

export type Status = { state: IntegrationState; detail?: string; detailTone?: 'warn' | 'err' };

export type Integration = {
  id: 'datev' | 'omd' | 'ids' | 'metal' | 'dkv' | 'meinshk' | 'lead_api';
  name: string;
  mark: string;
  category: Category;
  description: string;
  path: string;
};

export const INTEGRATIONS: Integration[] = [
  {
    id: 'datev',
    name: 'DATEV',
    mark: 'DATEV',
    category: 'Buchhaltung',
    description: 'Rechnungen, Belege, Debitoren und Kreditoren als DATEV-Export für deine Steuerkanzlei.',
    path: SETTINGS_PATH_DATEV,
  },
  {
    id: 'omd',
    name: 'Open Master Data',
    mark: 'OMD',
    category: 'Großhandel & Artikel',
    description: 'Live-Produktdaten, Preise und Bilder zu Artikelnummern im Stammdaten-Katalog.',
    path: SETTINGS_PATH_INTEGRATION_OMD,
  },
  {
    id: 'ids',
    name: 'IDS',
    mark: 'IDS',
    category: 'Großhandel & Artikel',
    description: 'Großhändler anbinden und den Warenkorb direkt aus dem Angebot übertragen.',
    path: SETTINGS_PATH_INTEGRATION_IDS,
  },
  {
    id: 'metal',
    name: 'Metallnotierungen',
    mark: 'Cu',
    category: 'Großhandel & Artikel',
    description: 'Kupfer- und Aluminiumzuschläge aus DATANORM-Katalogen mit aktuellen Notierungen berechnen.',
    path: SETTINGS_PATH_INTEGRATION_METAL,
  },
  {
    id: 'dkv',
    name: 'DKV Mobility',
    mark: 'DKV',
    category: 'Fuhrpark',
    description: 'Tankkarten-Transaktionen automatisch je Fahrzeug in den Fuhrpark übernehmen.',
    path: SETTINGS_PATH_INTEGRATION_DKV,
  },
  {
    id: 'meinshk',
    name: 'MeinSHK',
    mark: 'MH',
    category: 'Schnittstellen',
    description: 'Kunden, Projekte und Termine an die MeinSHK-App übergeben, Zeiten und Rapporte zurückholen.',
    path: SETTINGS_PATH_INTEGRATION_MEINSHK,
  },
  {
    id: 'lead_api',
    name: 'Externe API',
    mark: 'API',
    category: 'Schnittstellen',
    description: 'Projekte, Angebote und Dokumente per REST-API lesen und anlegen: für Website-Formulare, Baustellen-Apps und Automatisierungen.',
    path: SETTINGS_PATH_USERS_API,
  },
];

export const CTA: Record<IntegrationState, string> = {
  connected: 'Öffnen',
  incomplete: 'Fortsetzen',
  error: 'Prüfen',
  off: 'Einrichten',
};

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  const d = await res.json().catch(() => null);
  if (!res.ok) throw new Error(d?.error || 'Anfrage fehlgeschlagen.');
  return d as T;
}

export function formatDate(iso: string | null | undefined): string {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export async function omdStatus(): Promise<Status> {
  const d = await getJson<{ suppliers?: Array<{ configured: boolean }> }>('/api/settings/omd');
  const count = (d.suppliers ?? []).filter((s) => s.configured).length;
  return count > 0 ? { state: 'connected', detail: plural(count, 'Zugang', 'Zugänge') } : { state: 'off' };
}

export async function idsStatus(): Promise<Status> {
  const d = await getJson<{ connections?: Array<{ companyName: string; lastStatusCode: number | null }> }>('/api/settings/ids');
  const connections = d.connections ?? [];
  const failed = connections.filter((c) => c.lastStatusCode != null && c.lastStatusCode !== 0);
  if (failed.length > 0) {
    return { state: 'error', detail: `${failed[0].companyName}: Verbindungstest fehlgeschlagen`, detailTone: 'err' };
  }
  return connections.length > 0
    ? { state: 'connected', detail: plural(connections.length, 'Verbindung', 'Verbindungen') }
    : { state: 'off', detail: 'Shop-Zugang des Großhändlers nötig' };
}

export async function metalStatus(): Promise<Status> {
  const d = await getJson<{
    settings?: {
      copper_quote_eur_per_100kg?: number | null;
      aluminum_quote_eur_per_100kg?: number | null;
      metal_quote_source?: MetalQuoteSource | null;
      metal_quote_date?: string | null;
    };
  }>('/api/instance-settings');
  const s = d.settings ?? {};
  if (s.copper_quote_eur_per_100kg == null && s.aluminum_quote_eur_per_100kg == null) return { state: 'off' };
  const source = METAL_QUOTE_SOURCE_LABELS[s.metal_quote_source ?? 'manual'];
  const date = formatDate(s.metal_quote_date);
  return { state: 'connected', detail: `${source.charAt(0).toUpperCase()}${source.slice(1)}${date ? ` · ${date}` : ''}` };
}

export async function dkvStatus(): Promise<Status> {
  const d = await getJson<{
    settings?: {
      complete: boolean;
      lastStatusCode: number | null;
      lastStatusMessage: string | null;
      lastSyncStatus: 'ok' | 'error' | null;
      lastSyncAt: string | null;
      lastSyncStats?: { fuelImported?: number } | null;
    } | null;
    counts?: { fuel: number } | null;
  }>('/api/settings/dkv');
  const s = d.settings;
  if (!s) return { state: 'off', detail: 'Zugang vom DKV-Kundenservice nötig' };
  if (!s.complete) return { state: 'incomplete' };
  if (s.lastStatusCode != null && s.lastStatusCode !== 0) {
    return { state: 'error', detail: s.lastStatusMessage || 'Verbindungstest fehlgeschlagen', detailTone: 'err' };
  }
  if (s.lastSyncStatus === 'error') return { state: 'connected', detail: 'Letzter Abruf fehlgeschlagen', detailTone: 'warn' };
  const fuel = d.counts?.fuel ?? 0;
  if (s.lastSyncAt) return { state: 'connected', detail: `${plural(fuel, 'Tankung', 'Tankungen')} · ${formatDate(s.lastSyncAt)}` };
  return { state: 'connected', detail: 'Noch kein Abruf' };
}

export async function meinshkStatus(): Promise<Status> {
  const d = await getJson<{
    settings?: {
      complete: boolean;
      lastStatusCode: number | null;
      lastStatusMessage: string | null;
      lastSyncStatus: 'ok' | 'error' | null;
      lastSyncAt: string | null;
      lastReconcileAt: string | null;
    } | null;
    counts?: { employeesMapped: number; employeesUnmapped: number; projects: number } | null;
  }>('/api/settings/meinshk');
  const s = d.settings;
  if (!s) return { state: 'off', detail: 'Client-ID und Passwort aus MeinSHK nötig' };
  if (!s.complete) return { state: 'incomplete' };
  if (s.lastStatusCode != null && s.lastStatusCode !== 0) {
    return { state: 'error', detail: s.lastStatusMessage || 'Verbindungstest fehlgeschlagen', detailTone: 'err' };
  }
  if (!s.lastReconcileAt) return { state: 'connected', detail: 'Bestandsabgleich ausstehend', detailTone: 'warn' };
  if (s.lastSyncStatus === 'error') return { state: 'connected', detail: 'Letzter Abgleich mit Fehlern', detailTone: 'warn' };
  const mapped = d.counts?.employeesMapped ?? 0;
  const last = s.lastSyncAt ? ` · ${formatDate(s.lastSyncAt)}` : '';
  return { state: 'connected', detail: `${plural(mapped, 'Mitarbeiter zugeordnet', 'Mitarbeiter zugeordnet')}${last}` };
}

export async function leadApiStatus(): Promise<Status> {
  const d = await getJson<{ keys?: unknown[] }>('/api/me/api-keys');
  const count = Array.isArray(d.keys) ? d.keys.length : 0;
  return count > 0 ? { state: 'connected', detail: plural(count, 'Schlüssel', 'Schlüssel') } : { state: 'off' };
}

export async function datevStatus(enabled: boolean, configured: boolean): Promise<Status> {
  if (!enabled) return { state: 'off' };
  if (!configured) return { state: 'incomplete' };
  const d = await getJson<{ settings: { transfer_from: string | null } }>('/api/settings/datev');
  const overview = await loadDatevOverview(d.settings.transfer_from);
  if (overview.failing > 0) {
    return { state: 'connected', detail: plural(overview.failing, 'Rechnung mit DATEV-Fehler', 'Rechnungen mit DATEV-Fehler'), detailTone: 'warn' };
  }
  return {
    state: 'connected',
    detail: overview.lastExportAt ? `Letzter Export ${formatDate(overview.lastExportAt)}` : plural(overview.ready, 'Rechnung bereit', 'Rechnungen bereit'),
  };
}
